<template>
  <aside
    :class="mobileOpen ? 'fixed inset-x-0 bottom-0 z-50 mt-0 flex max-h-[85vh] rounded-t-2xl border-t' : 'h-14'"
    :style="mobileOpen ? { display: 'flex', flexDirection: 'column', position: 'fixed', top: 'auto', right: '0', bottom: '0', left: '0', marginTop: '0' } : undefined"
    class="api-sidebar relative mt-14 w-full shrink-0 border-b border-zinc-200 bg-white lg:mt-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:w-64 lg:overflow-y-auto lg:border-b-0 lg:border-r"
  >
    <div :class="mobileOpen ? 'mb-3 px-4 py-3' : 'fixed left-0 right-0 top-14 z-20 h-14 border-b border-zinc-200 px-4 py-2'" class="flex items-center justify-between bg-white lg:hidden">
      <span class="text-sm font-semibold text-zinc-950">{{ mobileOpen ? 'Navegação' : 'Grupos e endpoints' }}</span>
      <button type="button" class="inline-flex h-9 w-9 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-950" :aria-expanded="mobileOpen" :aria-label="mobileOpen ? 'Fechar navegação da API' : 'Abrir grupos e endpoints'" @click="toggleMobileSidebar">
        <X v-if="mobileOpen" class="h-5 w-5" />
        <List v-else class="h-5 w-5" />
      </button>
    </div>
    <div class="hidden p-6 lg:block">
      <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Documentação da API</p>
      <h1 class="mt-2 text-lg font-semibold text-zinc-950">{{ document.info?.title }}</h1>
      <p class="mt-2 text-sm text-zinc-500">v{{ document.info?.version }}</p>
    </div>
    <nav :class="mobileOpen ? 'block max-h-[60vh] overflow-y-auto' : 'hidden'" class="space-y-1 px-4 pb-4 lg:block lg:max-h-none lg:overflow-visible" aria-label="Seções da API">
      <details v-for="(tag, index) in groupedTags" :key="tag.name" :open="index === 0" class="group">
        <summary class="flex min-h-11 cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-100 hover:text-zinc-950 [&::-webkit-details-marker]:hidden">
          <span class="flex min-w-0 items-center gap-2">
            <span class="truncate">{{ tag.name }}</span>
            <span v-if="tag['x-badge']" class="shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700">{{ tag['x-badge'] }}</span>
          </span>
          <CaretDown class="h-4 w-4 shrink-0 text-zinc-400 transition group-open:rotate-180" aria-hidden="true" />
        </summary>
        <div class="ml-3 space-y-1 border-l border-zinc-200 pl-3">
          <a
            v-for="operation in tag.operations"
            :key="`${operation.method}-${operation.path}`"
            :href="`#${operationId(operation.method, operation.path)}`"
            class="flex min-h-10 items-start gap-2 py-2 text-xs leading-5 text-zinc-500 transition hover:text-zinc-950"
            @click.prevent.stop="scrollToOperation(operationId(operation.method, operation.path)); mobileOpen = false"
          >
            <span :class="['shrink-0 font-semibold uppercase', methodClass(operation.method)]">{{ operation.method }}</span>
            <span class="truncate">{{ operation.operation.summary || operation.path }}</span>
            <span v-if="operation.operation['x-badge']" class="shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700">{{ operation.operation['x-badge'] }}</span>
          </a>
        </div>
      </details>
    </nav>
    <div class="pointer-events-none absolute inset-x-0 bottom-0 hidden h-16 bg-gradient-to-t from-white via-white/90 to-transparent lg:block" aria-hidden="true"></div>
  </aside>
  <div v-if="mobileOpen" class="fixed inset-0 z-40 bg-zinc-950/30 lg:hidden" aria-hidden="true" @click="mobileOpen = false"></div>
</template>

<script setup lang="ts">
import type { ApiDocument } from "./apiTypes";
import { PhCaretDown as CaretDown, PhList as List, PhX as X } from "@phosphor-icons/vue";
import { ref } from "vue";

const props = defineProps<{ document: ApiDocument }>();
const mobileOpen = ref(false);
const toggleMobileSidebar = () => { mobileOpen.value = !mobileOpen.value; };
const methods = ["get", "post", "put", "patch", "delete", "options", "head"];

const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const operationId = (method: string, path: string) => slug(`${method}-${path}`);
const scrollToOperation = (id: string) => {
  const scroll = () => {
    const target = document.getElementById(id);
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    return true;
  };
  if (!scroll()) window.setTimeout(scroll, 50);
};
const methodClass = (method: string) => ({
  get: "text-blue-600",
  post: "text-green-600",
  put: "text-amber-600",
  patch: "text-purple-600",
  delete: "text-red-600",
  options: "text-cyan-600",
  head: "text-zinc-600",
}[method] || "text-zinc-600");
const tags: Array<{ name: string; description?: string; "x-badge"?: string }> = props.document.tags?.length
  ? props.document.tags
  : Array.from(new Set(Object.values(props.document.paths || {}).flatMap((pathItem) =>
      methods.flatMap((method) => pathItem[method]?.tags || [])
    ))).map((name) => ({ name }));
const groupedTags = tags.map((tag) => ({
  ...tag,
  operations: Object.entries(props.document.paths || {}).flatMap(([path, pathItem]) =>
    methods.filter((method) => pathItem[method]).map((method) => ({
      method,
      path,
      operation: pathItem[method],
    })).filter((item) => !item.operation.tags?.length || item.operation.tags.includes(tag.name))
  ),
}));
</script>

<script lang="ts">
export default { name: "ApiSidebar" };
</script>

<style scoped>
.api-sidebar {
  scrollbar-width: none;
}

.api-sidebar::-webkit-scrollbar {
  display: none;
}
</style>
